import { useApi, useToaster } from "@shopexperts/shared";
import { useMutation, useQueryClient } from "@tanstack/vue-query";

/**
 * Composable for deleting a draft lead (admin)
 * 
 * @example
 * ```ts
 * const { mutate, mutateAsync, isPending } = useDeleteDraftLead();
 * 
 * // Delete a draft lead
 * mutate(123);
 * ```
 */
export function useDeleteDraftLead() {
    const api = useApi();
    const toaster = useToaster();
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, isPending, error, reset } = useMutation({
        mutationFn: (id: number) => {
            return api.admin.draftLeads.deleteDraftLead({
                params: { id },
            });
        },
        onSuccess: (_, deletedId) => {
            queryClient.invalidateQueries({ queryKey: ["admin", "draft-leads"] });
            queryClient.removeQueries({ queryKey: ["admin", "draft-leads", deletedId] });
            toaster.create({
                title: "Draft lead deleted successfully",
                type: "success",
            });
        },
        onError: (error) => {
            toaster.create({
                title: "Failed to delete draft lead",
                description: error.message || "An error occurred",
                type: "error",
            });
            console.error("[useDeleteDraftLead] Error:", error);
        },
    });

    return {
        mutate,
        mutateAsync,
        isPending,
        error,
        reset,
    };
}
